/* eslint-disable no-nested-ternary */
import { faCrown } from '@fortawesome/free-solid-svg-icons/faCrown';
import React, { Fragment, useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import styled from 'styled-components';

import { Images } from '../../../assets';
import { AtiraEmpty } from '../../../components/AtiraEmpty';
import { Button } from '../../../components/Button';
import { Flex } from '../../../components/Flex';
import { Section } from '../../../components/Section';
import { SpinnerFullScreen } from '../../../components/SpinnerFullScreen';
import { Text } from '../../../components/Text';
import { atiraLinkTestIDs } from '../../../constants/test/atiralink.testIds';
import { CreateAtiraLinkDto } from '../../../model/atira-link/dto/CreateAtiraLinkDto';
import { AtiraLinkThemes } from '../../../model/atira-link/types/AtiraLinkThemes.enum';
import { AtiraLinkPlanType } from '../../../model/plan/types/AtiraLinkPlanType.enum';
import { AtiraLinkTheme } from '../../../model/theme/AtiraLinkTheme';
import { AtiraLinkPlanThemeTier } from '../../../model/theme/type/AtiraLinkPlanThemeTier';
import { usePlansContext } from '../../../plans-context';
import { atiraLinkSliceSelectors } from '../../../redux/atira-link/atira-link.selector';
import { AtiraLinkActions } from '../../../redux/atira-link/atira-link.slice';
import { useAppDispatch, useAppSelector } from '../../../redux/store';
import { userSliceSelectors } from '../../../redux/user/user.selector';
import { Rounded } from '../../../theme/Rounded';
import { Spacing } from '../../../theme/Spacing';
import { TemplatePreviewModal } from '../../shared/components/TemplatePreviewModal';
import { PhonePreviewIframe } from '../components/PhonePreviewIframe';

const CustomTemplate = styled(Flex)`
  border-radius: ${Rounded.xxl};
  width: 14rem;
  aspect-ratio: 59 / 125;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 0;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    bottom: 0;
    background-image: url(${Images.PreviewMobile});
    background-size: cover;
    background-position: center;
    pointer-events: none;
    width: 100%;
    height: 100%;
    z-index: 1;
  }
`;

const ThemeName = styled(Text)`
  color: transparent;
  background-clip: text;
  background-image: ${({ theme }) => `
  linear-gradient(${theme.main} 25%, ${theme.accent})
  `};
  font-weight: bold;
`;

const StyledButton = styled(Button)<{
  selected: boolean;
  isAvailable: boolean;
}>`
  background-color: ${({ theme, selected, isAvailable }) =>
    selected || isAvailable ? theme.main : theme.transparent};
  color: ${({ theme, selected, isAvailable }) =>
    selected || isAvailable ? theme.onMain : theme.main};
  border: ${({ theme }) => `1px solid ${theme.main}`};

  &:hover {
    background-color: ${({ theme }) => theme.main};
    color: ${({ theme }) => theme.onMain};
  }
`;

const ScrollWrapper = styled(Flex)`
  flex: 1;
  max-height: 85vh;
  overflow-y: auto;

  .infinite-scroll-component__outerdiv {
    display: flex;
    flex: 1;
  }

  .infinite-scroll-component {
    width: 100%;
    display: flex;
    gap: ${Spacing.m};
    flex-wrap: wrap;
    justify-content: space-evenly;
  }
`;

const PlanThemeAccess: Record<AtiraLinkPlanType, AtiraLinkPlanThemeTier[]> = {
  [AtiraLinkPlanType.PLAN_0]: [AtiraLinkPlanThemeTier.PLAN_0],
  [AtiraLinkPlanType.PLAN_1]: [
    AtiraLinkPlanThemeTier.PLAN_0,
    AtiraLinkPlanThemeTier.PLAN_1,
  ],
  [AtiraLinkPlanType.PLAN_2]: [
    AtiraLinkPlanThemeTier.PLAN_0,
    AtiraLinkPlanThemeTier.PLAN_1,
    AtiraLinkPlanThemeTier.PLAN_2,
  ],
};

export const AtiraLinkTemplatesTab: React.FC = () => {
  const [previewTheme, setPreviewTheme] = useState<AtiraLinkThemes | null>(
    null,
  );

  const {
    setValue,
    formState: { errors },
    watch,
  } = useFormContext<CreateAtiraLinkDto>();
  const selectedTheme = useWatch({ name: 'theme' });

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { setPlansModalVisible } = usePlansContext();
  const user = useAppSelector(userSliceSelectors.selectLoggedInUser)!;
  const themes = useAppSelector(atiraLinkSliceSelectors.selectThemes);

  const themesPage = useAppSelector(atiraLinkSliceSelectors.selectThemesPage);
  const themesPageHasNext = useAppSelector(
    atiraLinkSliceSelectors.selectThemesPageHasNext,
  );

  const themesLoading = useAppSelector(
    atiraLinkSliceSelectors.selectThemesLoading,
  );

  const atiraLink = watch();

  const getIsThemeAvailableForUserPlan = (theme: AtiraLinkTheme) => {
    const userFreeTrialPlan =
      user?.plan?.freeTrial?.hasUsed === true &&
      user?.plan?.freeTrial?.endsAt !== null
        ? user?.plan?.freeTrial?.freeTrialPlan
        : null;
    const userPlanType = userFreeTrialPlan || user?.plan?.type!;

    const themeTier = theme.tier;

    if (!PlanThemeAccess[userPlanType].includes(themeTier)) {
      return false;
    }

    return true;
  };

  const fetchMoreThemes = () => {
    dispatch(
      AtiraLinkActions.getThemes({
        meta: { page: themesPage, count: 9 },
      }),
    );
  };

  useEffect(() => {
    dispatch(AtiraLinkActions.resetTemplateMeta());
    dispatch(
      AtiraLinkActions.getThemes({
        meta: { page: 0, count: 9 },
      }),
    );
  }, [dispatch]);

  return (
    <Fragment>
      <Section title={t('common.templates')}>
        <Text>({t('atira_link.create.templates.template_title')})</Text>

        {themesLoading && !themes.length ? (
          <Flex
            justifyContent="center"
            alignItems="flex-start"
            height="100%"
            width="100%"
          >
            <SpinnerFullScreen />
          </Flex>
        ) : !themes.length ? (
          <AtiraEmpty description={t('common.template.empty')} />
        ) : (
          <ScrollWrapper id="scroll-wrapper">
            <InfiniteScroll
              next={fetchMoreThemes}
              hasMore={themesPageHasNext || false}
              loader={<SpinnerFullScreen />}
              dataLength={themes.length}
              scrollThreshold={0.75}
              style={{ overflow: 'visible' }} // don't remove
              scrollableTarget="scroll-wrapper"
            >
              {themes.map((theme, i) => {
                const themeAvailable = getIsThemeAvailableForUserPlan(theme);
                const canUseTheme =
                  themeAvailable || atiraLink?.theme === theme.name;

                return (
                  <Flex
                    flexDirection="column"
                    alignItems="center"
                    gap="s"
                    width="fit-content"
                    key={theme._id}
                  >
                    <Flex flexDirection="column" alignItems="center">
                      <ThemeName>{theme.name}</ThemeName>
                      <CustomTemplate>
                        <PhonePreviewIframe
                          key={theme._id}
                          theme={theme.name as AtiraLinkThemes}
                          onPreview={() =>
                            setPreviewTheme(theme.name as AtiraLinkThemes)
                          }
                          userAtiraLink={atiraLink}
                        />
                      </CustomTemplate>
                    </Flex>
                    {errors.theme?.message?.includes(theme.name) ? (
                      <Text
                        fontSize="s"
                        align="center"
                        style={{ width: '12rem' }}
                        color="danger"
                      >
                        {errors.theme?.message}
                      </Text>
                    ) : null}
                    <StyledButton
                      isAvailable={!themeAvailable}
                      onClick={() =>
                        canUseTheme
                          ? setValue('theme', theme.name as AtiraLinkThemes)
                          : setPlansModalVisible(true)
                      }
                      selected={
                        selectedTheme === (theme.name as AtiraLinkThemes)
                      }
                      icon={canUseTheme ? undefined : faCrown}
                      dataTestId={atiraLinkTestIDs.templates.theme(theme.name)}
                    >
                      {canUseTheme
                        ? `${theme.name.replaceAll('_', ' ')} ${selectedTheme === theme.name ? ' ✓' : ''}`
                        : t('common.upgrade')}
                    </StyledButton>
                  </Flex>
                );
              })}
            </InfiniteScroll>
          </ScrollWrapper>
        )}
      </Section>

      <TemplatePreviewModal
        selectedTheme={previewTheme}
        open={Boolean(previewTheme)}
        onClose={() => setPreviewTheme(null)}
      />
    </Fragment>
  );
};
